import type { StaticEntityType } from "@config";
import { useIsEditorContext } from "@editor-providers";
import {
	lazy,
	Suspense,
	type ComponentType,
	type CSSProperties,
	useCallback,
	useMemo,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { TextLibrary } from "../../text/components/Text.library";
import "../styles/table-cards.scss";
import { isAllowedInCell } from "../constants/allowed-in-cell-elements";
import {
	type ITableCell,
	type ITableCellStackProps,
	TableHeaderPosition,
} from "../types";
import { type ensureTableElementSettings } from "../utils/ensure-table-element-settings";
import { getTableBorderRadiusValue } from "../utils/get-table-border-radius-value";
import { sortRowOrder } from "../utils/sort-row-order";
import {
	applyGlobalValuesFilter,
	type ColumnFilter,
} from "../utils/apply-filters";
import { deriveColumnUniqueValues } from "../utils/derive-column-unique-values";
import {
	evaluateCalculation,
	formatCalculationResult,
} from "../utils/evaluate-calculation";
import { renderCalcResult } from "../utils/render-calc-formatting";
import { SortFilterSheet } from "./sort-filter-sheet";

// Lazy to break the circular init between the table and the generic element
// registry (Elements includes Table itself).
const LazyElementLibrary = lazy(() =>
	import("../../../components/Element.library").then((m) => ({
		default: m.ElementLibrary,
	})),
);

interface IProps {
	entity: StaticEntityType;
	elementSettings: ReturnType<typeof ensureTableElementSettings>;
	resolveCellContent?: (content: string) => string;
	// Editor-injected live in-cell element stack renderer (tables 2.0); cards
	// render outside the TableCtxProvider so it receives tableId explicitly.
	InCellStack?: ComponentType<ITableCellStackProps>;
}

interface ICardField {
	id: string;
	label?: ITableCell;
	value: ITableCell;
	// Precomputed content for calc cells (evaluated against the current row order).
	valueContent?: string;
}

interface ICard {
	fields: ICardField[];
	isAlt: boolean;
}

const FilterIcon = () => (
	<svg
		width="20"
		height="20"
		viewBox="0 0 14 14"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden
	>
		<path d="M2.33325 3.5H11.6666" />
		<path d="M3.5 7H10.5" />
		<path d="M5.25 10.5H8.75" />
	</svg>
);

const SortIcon = () => (
	<svg
		width="20"
		height="20"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden
	>
		<path d="M15 10v-5c0 -1.38 .62 -2 2 -2s2 .62 2 2v5m0 -3h-4" />
		<path d="M19 21h-4l4 -7h-4" />
		<path d="M4 15l3 3l3 -3" />
		<path d="M7 6v12" />
	</svg>
);

export const TableCards = ({
	entity,
	elementSettings,
	resolveCellContent,
	InCellStack,
}: IProps) => {
	const {
		tableData,
		tableRows,
		tableCols,
		tableHasHeader,
		tableHeaderPosition,
		tableCellTextOrientation,
		tableCellBgColor,
		tableCellTextColor,
		tableCellBorderColor,
		tableCellBorderWidth,
		tableCellBorderStyle,
		tableHasAlternatingRows,
		tableAlternatingRowsBgColor,
		tableAlternatingRowsTextColor,
		round,
		tableAllowSorting,
		tableAllowFiltering,
	} = elementSettings;

	const entityId = entity.id;
	const isEditor = useIsEditorContext();
	const readerMode = !isEditor;
	const sortGated = readerMode && tableAllowSorting === true;
	const filterGated = readerMode && tableAllowFiltering === true;

	const sortFilterCol = useMemo(() => {
		if (tableHasHeader && tableHeaderPosition === TableHeaderPosition.Left) {
			return 0;
		}
		if (tableHasHeader && tableHeaderPosition === TableHeaderPosition.Right) {
			return tableCols - 1;
		}
		return 0;
	}, [tableHasHeader, tableHeaderPosition, tableCols]);

	const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(
		null,
	);
	const [columnFilter, setColumnFilter] = useState<ColumnFilter | null>(null);
	const [menuOpen, setMenuOpen] = useState(false);

	const effectiveSortDirection = sortGated ? sortDirection : null;
	const effectiveColumnFilter = filterGated ? columnFilter : null;

	const uniqueValues = useMemo(() => {
		if (!filterGated) return [];
		const set = new Set<string>();
		for (let col = 0; col < tableCols; col++) {
			for (const v of deriveColumnUniqueValues(elementSettings, col)) {
				set.add(v);
			}
		}
		return Array.from(set).sort((a, b) => a.localeCompare(b));
	}, [filterGated, elementSettings, tableCols]);

	const rowOrder = useMemo(() => {
		const sorted = effectiveSortDirection
			? sortRowOrder(elementSettings, sortFilterCol, effectiveSortDirection)
			: null;
		const filterActive =
			!!effectiveColumnFilter && effectiveColumnFilter.kind === "values";
		if (!sorted && !filterActive) return undefined;
		const base = sorted ?? Array.from({ length: tableRows }, (_, i) => i);
		return filterActive && effectiveColumnFilter?.kind === "values"
			? applyGlobalValuesFilter(
					base,
					elementSettings,
					effectiveColumnFilter.selected,
				)
			: base;
	}, [
		effectiveSortDirection,
		effectiveColumnFilter,
		elementSettings,
		sortFilterCol,
		tableRows,
	]);

	const cards = useMemo<ICard[]>(() => {
		const cellAt = (row: number, col: number): ITableCell | undefined =>
			tableData[row * tableCols + col];

		const cellById = new Map(tableData.map((c) => [c.id, c]));
		const resolve = (cell: ITableCell): ITableCell =>
			cell.mergedInto ? (cellById.get(cell.mergedInto) ?? cell) : cell;

		const rowIterationOrder =
			rowOrder ?? Array.from({ length: tableRows }, (_, i) => i);

		// Calc cells render their evaluated result (against the current row order)
		// instead of raw content.
		const calcContentFor = (
			cell: ITableCell,
			cellRow: number,
			cellCol: number,
		): string | undefined => {
			if (!cell.calculation) return undefined;
			const result = evaluateCalculation(cell.calculation.formula, {
				tableData,
				tableCols,
				tableRows,
				rowOrder: rowIterationOrder,
				cellCoord: { col: cellCol, row: cellRow },
			});
			return renderCalcResult(
				cell.calcFormatHtml,
				formatCalculationResult(result),
			);
		};

		const headerPos = tableHasHeader ? tableHeaderPosition : null;
		const isSideHeader =
			headerPos === TableHeaderPosition.Left ||
			headerPos === TableHeaderPosition.Right;

		if (isSideHeader) {
			const headerCol =
				headerPos === TableHeaderPosition.Left ? 0 : tableCols - 1;
			const result: ICard[] = [];
			for (let col = 0; col < tableCols; col++) {
				if (col === headerCol) continue;
				const fields: ICardField[] = [];
				for (const row of rowIterationOrder) {
					const cell = cellAt(row, col);
					if (!cell) continue;
					const labelCell = cellAt(row, headerCol);
					fields.push({
						id: cell.id,
						value: resolve(cell),
						valueContent: calcContentFor(cell, row, col),
						label: labelCell ? resolve(labelCell) : undefined,
					});
				}
				if (fields.length) result.push({ fields, isAlt: false });
			}
			return result;
		}

		const headerRow =
			headerPos === TableHeaderPosition.Top
				? 0
				: headerPos === TableHeaderPosition.Bottom
					? tableRows - 1
					: null;

		const result: ICard[] = [];
		rowIterationOrder.forEach((row, visualIdx) => {
			if (row === headerRow) return;
			const fields: ICardField[] = [];
			for (let col = 0; col < tableCols; col++) {
				const cell = cellAt(row, col);
				if (!cell) continue;
				const labelCell =
					headerRow !== null ? cellAt(headerRow, col) : undefined;
				fields.push({
					id: cell.id,
					value: resolve(cell),
					valueContent: calcContentFor(cell, row, col),
					label: labelCell ? resolve(labelCell) : undefined,
				});
			}
			if (fields.length)
				result.push({
					fields,
					isAlt: tableHasAlternatingRows && visualIdx % 2 !== 0,
				});
		});
		return result;
	}, [
		tableData,
		tableRows,
		tableCols,
		tableHasHeader,
		tableHeaderPosition,
		tableHasAlternatingRows,
		rowOrder,
	]);

	// In-cell element children (tables 2.0) grouped by cellId from the entity's
	// nested published JSON — cards render each cell's stack read-only.
	const cellChildren = useMemo(() => {
		const children = entity.children;
		if (!children?.length) return undefined;
		const byCell: Record<string, StaticEntityType[]> = {};
		children.forEach((child) => {
			const cellId = (child.settings as { cellId?: string })?.cellId;
			if (!cellId || !isAllowedInCell(child.settings.name as string)) return;
			(byCell[cellId] ??= []).push(child);
		});
		return Object.keys(byCell).length ? byCell : undefined;
	}, [entity.children]);

	const renderContent = (cell: ITableCell, contentOverride?: string) => (
		<>
			<TextLibrary
				editorData={{
					isAdmin: false,
					content:
						contentOverride ??
						(resolveCellContent
							? resolveCellContent(cell.content)
							: cell.content),
				}}
				entity={{
					settings: {},
					id: `${entityId}-${cell.id}`,
					children: [],
					type: "element",
				}}
			/>
			{InCellStack ? (
				<InCellStack cellId={cell.id} tableId={entityId} />
			) : cellChildren?.[cell.id]?.length ? (
				<div className="table-cell-children tw-flex tw-w-full tw-flex-col">
					<Suspense fallback={null}>
						{cellChildren[cell.id].map((child) => (
							<LazyElementLibrary key={child.id} entity={child} />
						))}
					</Suspense>
				</div>
			) : null}
		</>
	);

	const handleApply = useCallback(
		(sort: "asc" | "desc" | null, filter: ColumnFilter | null) => {
			setSortDirection(sort);
			setColumnFilter(filter);
			setMenuOpen(false);
		},
		[],
	);

	const handleReset = useCallback(() => {
		setSortDirection(null);
		setColumnFilter(null);
		setMenuOpen(false);
	}, []);

	const showToolbar = sortGated || filterGated;
	if (!cards.length && !showToolbar) return null;

	return (
		<div
			className="table-cards-root"
			style={{ direction: tableCellTextOrientation }}
		>
			{showToolbar && (
				<div className="table-cards-mobile-toolbar tw-flex tw-items-center tw-w-full tw-my-3">
					<button
						type="button"
						data-filter-trigger
						aria-haspopup="dialog"
						aria-expanded={menuOpen}
						onClick={() => setMenuOpen((p) => !p)}
						data-active={
							effectiveSortDirection || effectiveColumnFilter ? "" : undefined
						}
						className="tw-flex tw-h-10 tw-items-center tw-justify-center tw-gap-0.5 tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-[rgba(18,3,62,0.12)] tw-rounded-lg tw-cursor-pointer data-[active]:tw-text-[#610cff] data-[active]:tw-border-[#610cff]"
					>
						<span className="tw-text-blue-base tw-flex tw-flex-shrink-0">
							{sortGated && !filterGated ? <SortIcon /> : <FilterIcon />}
						</span>
						<span className="tw-px-1.5 tw-font-medium tw-text-base tw-leading-6 tw-text-blue-base">
							{sortGated && filterGated
								? "Filter & Sort"
								: filterGated
									? "Filter"
									: "Sort"}
						</span>
					</button>
				</div>
			)}

			{menuOpen &&
				createPortal(
					<>
						<div
							aria-hidden
							className="tw-fixed tw-inset-0 tw-bg-black/30"
							style={{ zIndex: 9998 }}
						/>
						<div
							style={{
								position: "fixed",
								left: 8,
								right: 8,
								bottom: 8,
								zIndex: 9999,
							}}
						>
							<SortFilterSheet
								sortEnabled={sortGated}
								filterEnabled={filterGated}
								columnKind="text"
								values={uniqueValues}
								initialSort={effectiveSortDirection}
								initialFilter={effectiveColumnFilter}
								onApply={handleApply}
								onReset={handleReset}
								onClose={() => setMenuOpen(false)}
							/>
						</div>
					</>,
					document.querySelector(".sd-presentation") ?? document.body,
				)}

			{cards.map(({ fields, isAlt }, cardIdx) => (
				<article
					className="table-cards-card"
					key={fields[0]?.id ?? cardIdx}
					style={
						{
							background: isAlt
								? tableAlternatingRowsBgColor
								: tableCellBgColor,
							border: `${tableCellBorderWidth}px ${tableCellBorderStyle} ${tableCellBorderColor}`,
							borderRadius: `${getTableBorderRadiusValue(round)}px`,
							"--table-cards-color": isAlt
								? tableAlternatingRowsTextColor
								: tableCellTextColor,
						} as CSSProperties
					}
				>
					{fields.map(({ id, label, value, valueContent }) => (
						<div className="table-cards-pair" key={id}>
							{label && (
								<div className="table-cards-label">{renderContent(label)}</div>
							)}
							<div className="table-cards-value">
								{renderContent(value, valueContent)}
							</div>
						</div>
					))}
				</article>
			))}
		</div>
	);
};
